// frontend/src/components/DepartmentSwitcher.js
// ヘッダーに置く部署切替 UI (切替のみ)。
//   - 現在の部署名+病院名を表示
//   - 同一病院配下の部署に切替
//   - 部署の追加は 運営パネル からのみ可能 (= 本コンポーネントでは行わない)
//
// Header.js で admin ロールの場合のみマウント想定。

import { useState, useRef, useEffect } from 'react';
import { Building2, Check, ChevronDown } from 'lucide-react';
import { useOrganization } from '../contexts/OrganizationContext';

export default function DepartmentSwitcher() {
  const {
    currentOrganization,
    currentHospital,
    siblingOrganizations,
    switchOrganization,
  } = useOrganization();
  const [open, setOpen] = useState(false);
  const popupRef = useRef(null);

  // 外側クリックで閉じる
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!currentOrganization?.id) return null;
  // 兄弟部署が 1 件以下 (= 切替えても意味がない) なら非表示
  if ((siblingOrganizations || []).length <= 1) return null;

  const handleSelect = (orgId) => {
    if (orgId === currentOrganization.id) {
      setOpen(false);
      return;
    }
    switchOrganization(orgId);
    setOpen(false);
    // 切替直後は現在ページがその組織前提の state を持っているため、再読み込みする
    setTimeout(() => window.location.reload(), 50);
  };

  return (
    <div style={{ position: 'relative' }} ref={popupRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={styles.trigger}
        title="部署切替"
      >
        <Building2 size={16} style={{ color: '#4338ca' }} />
        <div style={styles.triggerLabel}>
          {currentHospital?.name && (
            <span style={styles.hospitalName}>{currentHospital.name}</span>
          )}
          <span style={styles.deptName}>{currentOrganization.name || currentOrganization.id}</span>
        </div>
        <ChevronDown size={14} style={{ color: '#6b7280' }} />
      </button>

      {open && (
        <div style={styles.popup}>
          <div style={styles.popupHeader}>
            <span style={styles.popupTitle}>部署切替</span>
            {currentHospital?.name && (
              <span style={styles.popupHospital}>{currentHospital.name}</span>
            )}
          </div>
          <div style={styles.list}>
            {(siblingOrganizations || []).map((org) => {
              const active = org.id === currentOrganization.id;
              return (
                <button
                  key={org.id}
                  onClick={() => handleSelect(org.id)}
                  style={{
                    ...styles.item,
                    background: active ? '#eef2ff' : '#fff',
                    fontWeight: active ? 700 : 500,
                    color: active ? '#4338ca' : '#111827',
                  }}
                >
                  <span>{org.name || org.id}</span>
                  {active && <Check size={14} />}
                </button>
              );
            })}
          </div>
          <div style={styles.popupFooter}>
            <span style={styles.footerHint}>部署の追加は運営パネルから</span>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  trigger: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 12px',
    background: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    cursor: 'pointer',
    maxWidth: 220,
  },
  triggerLabel: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    minWidth: 0,
    flex: 1,
  },
  hospitalName: {
    fontSize: '0.65rem',
    color: '#6b7280',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
  },
  deptName: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#111827',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
  },
  popup: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: 8,
    width: 'min(300px, 90vw)',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
    zIndex: 10001,
    maxHeight: '60vh',
    display: 'flex',
    flexDirection: 'column',
  },
  popupHeader: {
    padding: '10px 14px',
    borderBottom: '1px solid #f3f4f6',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  popupTitle: {
    fontWeight: 700,
    fontSize: '0.85rem',
    color: '#111827',
  },
  popupHospital: {
    fontSize: '0.7rem',
    color: '#6b7280',
  },
  list: {
    overflowY: 'auto',
    flex: 1,
    padding: '4px 0',
  },
  item: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 14px',
    border: 'none',
    borderBottom: '1px solid #f3f4f6',
    cursor: 'pointer',
    textAlign: 'left',
    fontSize: '0.85rem',
  },
  popupFooter: {
    padding: '8px 14px',
    borderTop: '1px solid #f3f4f6',
  },
  footerHint: {
    fontSize: '0.7rem',
    color: '#9ca3af',
  },
};
